import { refreshFamily } from './cadence.ts';
import { matchRefreshPreferred } from './quality.ts';

/**
 * Display refresh matching through the Google TV wrapper.
 *
 * Google TV boxes drive HDMI at 60 Hz unless an app asks otherwise, and a
 * WebView never asks. European broadcast is 50 fps: on a 60 Hz output every
 * fifth frame stays up for two refreshes, ten times a second -- a regular
 * stutter in every pan, on every tile at once, with no frame dropped. It looks
 * like weak hardware and no amount of decoder headroom fixes it. The wrapper
 * can switch the output to a matching mode; this module decides when.
 */

function bridge() {
  const b = window.MultiviewTV;
  return b && typeof b.setFrameRate === 'function' ? b : null;
}

/** True inside a TV app new enough to switch display modes. */
export function canMatchRefresh(): boolean {
  return bridge() !== null;
}

let requested: number | null = null;

/**
 * Asks for a display mode matching content at `fps`. Each change costs a
 * second or two of black while the TV resyncs, so it happens once per rate
 * family and not back and forth: the mode is kept when leaving a match, and
 * Android drops the request by itself when the app closes.
 */
export function matchFrameRate(fps: number): void {
  const b = bridge();
  if (!b || !matchRefreshPreferred()) return;
  const family = refreshFamily(fps);
  if (family === null || family === requested) return;
  requested = family;
  try {
    b.setFrameRate!(family);
  } catch { /* an older wrapper without the method: nothing to do */ }
}

/** Gives the display back to the system default (setting switched off). */
export function resetFrameRate(): void {
  const b = bridge();
  requested = null;
  try {
    b?.setFrameRate!(0);
  } catch { /* nothing to reset */ }
}

let measuredHz: number | null = null;
let measuring = false;

/**
 * The display mode for telemetry: what the TV app reports, or else a refresh
 * rate measured once from animation frames (a second of rAF, then never
 * again -- it is a diagnostic, not something to keep running beside video).
 */
export function displayDescription(): string | null {
  try {
    const mode = window.MultiviewTV?.displayMode?.();
    if (mode) return mode;
  } catch { /* fall through to the measurement */ }

  if (measuredHz === null && !measuring) {
    measuring = true;
    let frames = 0;
    let start = 0;
    const step = (t: number) => {
      if (start === 0) start = t;
      frames++;
      if (t - start < 1000) {
        requestAnimationFrame(step);
      } else {
        measuredHz = Math.round(((frames - 1) * 1000) / (t - start));
        measuring = false;
      }
    };
    requestAnimationFrame(step);
  }
  return measuredHz === null ? null : `~${measuredHz}Hz`;
}
